/**
 * RealtimeVoiceMode.jsx — Project One Arvexis
 * 
 * A WebRTC-based realtime voice conversation with Arvexis.
 * Uses OpenAI's Realtime API for low-latency, bidirectional audio streaming.
 * 
 * PRIVACY MODEL (Private by Default):
 * - Transcripts captured during voice sessions
 * - After session ends, user has 6 hours to decide
 * - "Save Transcript" → persists indefinitely
 * - "Let It Disappear" → deleted after retention period
 * - Relationship memory updates regardless of transcript status
 * 
 * Key features:
 * - No tap-to-talk — continuous listening
 * - Server-side VAD — automatic turn detection
 * - Native barge-in — interrupt naturally
 * - Streaming audio — lower perceived latency
 * - Technology disappears — just conversation
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mic, MicOff, Infinity as InfinityIcon, Clock } from 'lucide-react';
import ArvexisPresence from './ArvexisPresence';
import VoicePrivacyModal from './VoicePrivacyModal';
import { useMemoryAdmission } from './MemoryAdmission';
import { useAudioAnalyser } from '../hooks/useAudioAnalyser';
import { ACTIVITY, activityLabel } from '../utils/activityVocabulary';
import { requestDeviceLocation, locationPermissionState } from '../utils/deviceLocation';
import {
  evaluateMemberTranscript,
  selfEchoCorrections,
  PLAYBACK_OVERLAP_GRACE_MS
} from '../utils/memberTranscriptGate';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Silent session refresh. The access token lives 15 minutes, so a member who returns to Talk
// after a while arrives with an expired one. Every recovery call used to treat that 401 as
// "nothing to recover" and stayed quiet — which is exactly how an interrupted conversation was
// never offered back. Message already refreshes and retries; Talk must too.
const fetchWithAuthRetry = async (url, options = {}) => {
  let response = await fetch(url, options);
  if (response.status === 401) {
    const refreshed = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include'
    });
    if (refreshed.ok) {
      response = await fetch(url, options);
    }
  }
  return response;
};

// DURABILITY (Talk transcript fix). A turn that could not be written waits and is retried on a
// widening delay rather than being dropped. The spoken conversation is never interrupted by it.
const TRANSCRIPT_RETRY_DELAYS_MS = [2000, 5000, 15000, 30000];

// The turn's own identity, minted where the turn happens, so a retried write is recognised as
// the same turn instead of becoming a second copy of it.
const newTranscriptTurnId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return `turn_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

// The Talk session's own identity, used by the backend to find the transcript this call writes
// into. Same guarded shape as the turn id above, for the same reason.
const newVoiceSessionId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `voice_${crypto.randomUUID()}`;
  } catch (e) { /* fall through */ }
  return `voice_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

// Connection states
// A transient WebRTC dip is normal on a route change; a dip that never recovers is not.
const DISCONNECT_GRACE_MS = 10000;
// TALK CONNECTION TRUTH — bounded connection deadline.
//
// Measured on a physical iPhone: ICE reached `checking` and never transitioned again, so DTLS
// never completed, so the data channel never opened. CONNECTED is reachable from exactly one
// place — dataChannel.onopen — and nothing else bounded that wait: peer state stayed
// 'connecting' (never 'failed', never 'disconnected'), so no existing error path could fire and
// "Connecting..." was structurally permanent.
//
// The clock starts when the data channel is created, i.e. at the beginning of the transport
// phase, so it measures the connection and never the member's microphone-permission decision.
// Observed successful sessions opened the channel 2.4s after that point; 12s is a wide margin
// over the transport, and a real boundary rather than an unbounded promise.
const CONNECT_DEADLINE_MS = 12000;

// TALK ICE REPAIR — C1: candidate paths.
//
// This peer connection was created with no ICE configuration at all, from the file's first
// commit. With no STUN server the browser can only ever offer host candidates, so Talk depended
// on a direct route existing and being usable. On 2026-09-17 a physical iPhone gathered ZERO
// candidates of any type — {host: 0, srflx: 0, relay: 0} — twenty-three minutes after the same
// device held a full five-minute conversation on the same build. Gathering never left
// 'gathering', so the data channel could not open and the 12s deadline fired.
//
// Public STUN only. No TURN: a relay is a paid third-party service and a separate Founder
// decision. STUN cannot rescue a network that blocks direct connectivity outright, and this
// does not pretend otherwise — it restores the reflexive path that was never there.
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

// TALK ICE REPAIR — C2: the offer must carry candidates.
//
// The SDP exchange here is non-trickle: one POST, one answer, no channel to add candidates
// afterwards. The offer was being sent ~0.15s after createOffer(), before gathering had
// produced anything, so whether it carried a usable candidate was luck. On the successful
// session gathering completed 0.3s in, ahead of the answer. On the failures it never completed
// and the offer went out empty, which no later event could repair.
//
// So gathering is awaited before the POST — bounded, and deliberately WELL INSIDE
// CONNECT_DEADLINE_MS, which is not extended. If the budget is exhausted the attempt proceeds
// with whatever was gathered rather than stalling: a 12s honest failure is still the contract.
const ICE_GATHERING_BUDGET_MS = 4000;

// TALK ICE REPAIR — C3: gathering that produces NOTHING is not a slow network, and waiting
// longer cannot fix it.
//
// On the Founder's iPhone, intermittently, Safari's ICE agent returns zero candidates of ANY
// type — not even host candidates, which need no network, no STUN and no TURN. A non-trickle
// offer with no candidates gives the provider nowhere to answer, so the attempt is already lost
// the moment it is posted; it then sat out the full connect deadline before saying so, and spent
// a provider call on the way. When that state is seen, ICE is restarted once — the standards-
// defined way to make the agent gather again — on a SHORTER budget, so the retry and the first
// wait together still sit well inside the unchanged CONNECT_DEADLINE_MS. If the agent still
// produces nothing, Talk says so immediately instead of pretending to connect for 12 seconds.
const ICE_REGATHER_BUDGET_MS = 2500;

// N1 DIAGNOSTIC INSTRUMENTATION — telemetry only, and the reason it exists: after a device
// attempt failed there was no way to prove WHICH Talk build had executed on the phone. This
// stamp rides along with every voice diagnostic. It changes nothing about the connection.
const TALK_BUILD_STAMP = 'talk-2026-09-23-n1-repairA';

// N1 — candidate evidence read from the local description Safari itself produced. TYPE COUNTS
// ONLY: no address, no port, no foundation and no candidate string is read or recorded.
const sdpCandidateEvidence = (sdp) => {
  if (!sdp) return null;
  const typeCounts = { host: 0, srflx: 0, prflx: 0, relay: 0, other: 0 };
  let lineCount = 0;
  sdp.split('\n').forEach((line) => {
    if (!line.startsWith('a=candidate')) return;
    lineCount += 1;
    const type = / typ (\w+)/.exec(line)?.[1];
    if (type && type in typeCounts) typeCounts[type] += 1;
    else typeCounts.other += 1;
  });
  return {
    line_count: lineCount,
    type_counts: typeCounts,
    end_of_candidates: sdp.includes('a=end-of-candidates'),
  };
};

// N1 — the third, independent measurement: what Safari's own statistics say it created,
// regardless of whether any onicecandidate event was delivered. Types only.
const statsCandidateEvidence = async (pc) => {
  if (!pc?.getStats) return null;
  try {
    const typeCounts = { host: 0, srflx: 0, prflx: 0, relay: 0, other: 0 };
    let localCandidateCount = 0;
    const stats = await pc.getStats();
    stats.forEach((report) => {
      if (report.type !== 'local-candidate') return;
      localCandidateCount += 1;
      const type = report.candidateType;
      if (type && type in typeCounts) typeCounts[type] += 1;
      else typeCounts.other += 1;
    });
    return { local_candidate_count: localCandidateCount, type_counts: typeCounts };
  } catch (e) {
    return { unavailable: true };
  }
};

// Phase 7 Bundle 5 — how long the orb takes to come back to calm after she has spoken.
const ORB_SETTLE_MS = 1200;

// Phase 7 Bundle 5 — the member-facing words for the two real Talk boundaries, in the
// Founder-approved Talk-continuity register. No percentages, no token counts, no minutes, no
// countdown, no suggestion that anything is wrong. Fence 1 is an invitation that can be left
// alone; Fence 2 is the truth that the conversation has to move shortly.
const HANDOFF_COPY = {
  en: {
    context: {
      title: 'Continue in a fresh space?',
      body: "We've been talking for a while. I can bring what we're working on with us.",
      primary: 'Continue',
      secondary: 'Later',
    },
    boundary: {
      title: "Let's keep going together",
      body: 'This call will end soon. I can move us into a fresh space so we can continue.',
      primary: 'Continue Talk',
      secondary: 'Stay for now',
    },
    failed: {
      title: "The next space didn't open",
      body: "We're still here — we can keep talking, or try again.",
      primary: 'Try again',
    },
    moving: 'Moving with you…',
    dismiss: 'Dismiss',
  },
  es: {
    context: {
      title: '¿Seguimos en un espacio nuevo?',
      body: 'Llevamos un rato hablando. Puedo llevarme con nosotros lo que estamos trabajando.',
      primary: 'Continuar',
      secondary: 'Más tarde',
    },
    boundary: {
      title: 'Sigamos juntos',
      body: 'Esta llamada terminará pronto. Puedo llevarnos a un espacio nuevo para continuar.',
      primary: 'Continuar',
      secondary: 'Quedarnos aquí',
    },
    failed: {
      title: 'El siguiente espacio no se abrió',
      body: 'Seguimos aquí: podemos seguir hablando, o intentarlo de nuevo.',
      primary: 'Intentar de nuevo',
    },
    moving: 'Vamos contigo…',
    dismiss: 'Cerrar',
  },
};

// Fence 2 may be set aside, but the call really is going to end, so it returns once after a calm
// pause rather than either nagging every few seconds or going silent about it. She is never asked
// to say it a second time.
const BOUNDARY_HUSH_MS = 60000;

const STATE = {
  INITIALIZING: 'initializing',  // Setting up WebRTC
  CONNECTING: 'connecting',      // Connecting to Realtime API
  CONNECTED: 'connected',        // Connected, conversation active
  LISTENING: 'listening',        // Arvexis is listening to user
  THINKING: 'thinking',          // Arvexis is processing/thinking
  SPEAKING: 'speaking',          // Arvexis is speaking
  ERROR: 'error',                // Connection failed
  CLOSED: 'closed'               // Session ended
};

// B2 — how long a close attempt may hold the close latch before the control accepts another tap.
// A normal close resolves in well under a second; this only ever releases a stalled one.
const CLOSE_LATCH_STALL_MS = 6000;


/**
 * RealtimeVoiceMode — Continuous conversational voice with Arvexis
 * 
 * Enter once. Remain together. Speak naturally. Listen naturally.
 * Interrupt naturally. Continue naturally. Leave naturally.
 */
const RealtimeVoiceMode = ({ 
  onClose, 
  lang = 'en',
  userId = null 
}) => {
  // Connection state
  const [state, setState] = useState(STATE.INITIALIZING);
  const [error, setError] = useState(null);
  // Capacity, not a fault: the server's own neutral statement that this period's Talk time is
  // used, with the reset date and whether Message remains available. Rendered instead of an
  // error, and with no retry — there is nothing to retry against.
  const [capacityLimit, setCapacityLimit] = useState(null);

  // 4.3 — truthful activity on a voice-first surface. Listening, thinking and speaking stay
  // orb-only; a caption appears only for work the member cannot see or hear happening — a live
  // lookup or a read of their own record — and only while that operation is actually running.
  const [talkActivity, setTalkActivity] = useState(null);
  
  // Audio state
  const [isMuted, setIsMuted] = useState(false);
  const [isArvexisSpeaking, setIsArvexisSpeaking] = useState(false);
  
  // Remote audio stream for analysis (Arvexis's voice)
  const [remoteStream, setRemoteStream] = useState(null);
  
  // Privacy modal state
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [transcriptInfo, setTranscriptInfo] = useState({});
  const [safetyNotice, setSafetyNotice] = useState(null);
  // DURABILITY (Talk transcript fix). A turn is spoken browser-side and persisted by a separate
  // authenticated POST. When that POST fails — an expired cookie is the real case — the turn
  // waits here in the order it was spoken instead of being dropped, and the member is told the
  // conversation is not being saved rather than being left to believe it is.
  const pendingTurnsRef = useRef([]);
  const flushingTranscriptRef = useRef(false);
  const transcriptRetryTimerRef = useRef(null);
  const transcriptRetryIndexRef = useRef(0);
  const [transcriptDegraded, setTranscriptDegraded] = useState(false);
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);

  // 3.9 — the Talk continuity write starts as the end-of-session response returns, so this is
  // the only moment it can be confirmed truthfully. A late result shows nothing.
  const { admission: memoryAdmission, check: checkMemoryAdmission } = useMemoryAdmission();
  
  // Diagnostic telemetry state (for iOS debugging)
  const [diagnosticStage, setDiagnosticStage] = useState('init');
  const diagnosticStartTime = useRef(Date.now());
  
  // Transcript tracking
  const sessionIdRef = useRef(null);
  // 4.1 Cluster 1 — whether the app-wide browser permission is ALREADY granted. Read without
  // prompting (WebKit reports 'granted' truthfully). When it is, Talk acquires a fresh fix
  // itself before a location-dependent lookup instead of leaving that to the model, which is
  // how a granted permission ended up unused on Talk while Message used it correctly.
  const locationGrantedRef = useRef(false);
  const lastMemberTextRef = useRef(null);
  const transcriptIdRef = useRef(null);
  // Recovery of an interrupted Talk deliberately does NOT live here — it belongs to the Talk
  // surface (see TalkRecoveryOffer), because it must be reachable without a live session.
  const isClosingRef = useRef(false); // Guard against double close
  const closeLatchedAtRef = useRef(0); // B2: when the close latch was taken
  
  // WebRTC refs
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const localStreamRef = useRef(null);
  
  // Mount ref for cleanup
  const isMountedRef = useRef(true);
  // Audio-route resilience (3.5.5): one mic re-acquisition per session, and handles to
  // detach so nothing outlives the session that created it.
  const micReacquiredRef = useRef(false);
  const deviceChangeHandlerRef = useRef(null);
  const disconnectGraceRef = useRef(null);
  // Guard against double initialization (React Strict Mode)
  const isInitializingRef = useRef(false);

  // Talk connection truth: the bounded-connection deadline, whether this attempt's channel ever
  // opened, and an attempt generation so a connection that expired can never come back and
  // change what the member is looking at.
  const connectDeadlineRef = useRef(null);
  const dataChannelOpenedRef = useRef(false);
  const attemptIdRef = useRef(0);
  // Candidate TYPE counts only — never a candidate string, never an address.
  const candidateTypeCountsRef = useRef({ host: 0, srflx: 0, relay: 0, other: 0 });

  // ---------------------------------------------------------------------------------------
  // TALK METERING (Founder-authorized). The grant is the server's opaque identity for this
  // Talk's usage. It is deliberately NOT the transcript session id.
  //
  // Metering begins only when WebRTC genuinely reaches 'connected' — minting a token and
  // failing to connect costs the member nothing. Heartbeats report that the call is still live;
  // the SERVER decides how much time that is worth, and clamps it. The client cannot inflate
  // usage, only fail to report it.
  // ---------------------------------------------------------------------------------------
  const talkGrantRef = useRef(null);
  const meterIntervalMsRef = useRef(20000);
  const meterSeqRef = useRef(0);
  const meterTimerRef = useRef(null);
  const meterOpenedRef = useRef(false);
  // Set when the server says the allowance is spent. The current conversational turn is allowed
  // to finish; Arvexis is never cut off mid-sentence.
  const meterMustCloseRef = useRef(false);

  // Phantom-utterance repair: what Arvexis was saying, and whether the segment now being
  // transcribed was captured while she was audible. Read synchronously inside the event
  // handler, so refs rather than state.
  const assistantAudibleRef = useRef(false);
  const assistantAudioStoppedAtRef = useRef(0);
  const recentAssistantTextRef = useRef([]);
  const memberTurnRef = useRef(null);
  
  // Audio analysis for orb reactivity
  // Analyze the remote audio stream (Arvexis's voice) to drive orb animations
  const { volume: arvexisVolume } = useAudioAnalyser(remoteStream, {
    enabled: state === STATE.CONNECTED || state === STATE.SPEAKING || state === STATE.LISTENING,
    smoothingFactor: 0.85, // Smooth for graceful animations
    fftSize: 256 // Efficient for mobile
  });

  // ---------------------------------------------------------------------------------------
  // PHASE 7 BUNDLE 5 — LIVING ORB, LISTENING.
  //
  // The member's own voice, so the orb is attentive while they speak. Deliberately enabled
  // for the WHOLE connected session rather than only while listening: the analyser opens one
  // AudioContext per stream and iOS will not give us an unbounded number of them, so toggling
  // it per turn would fail on a long call. It is the ORB that ignores the value outside
  // listening, not the analyser that restarts.
  // ---------------------------------------------------------------------------------------
  const [localStream, setLocalStream] = useState(null);
  const { volume: memberVolumeRaw } = useAudioAnalyser(localStream, {
    enabled: state === STATE.CONNECTED || state === STATE.SPEAKING
      || state === STATE.LISTENING || state === STATE.THINKING,
    smoothingFactor: 0.85,
    fftSize: 256
  });

  // A calm return after she has spoken, rather than an instant snap back to rest.
  const [settlingUntil, setSettlingUntil] = useState(0);

  // ---------------------------------------------------------------------------------------
  // PHASE 7 BUNDLE 5 — THE REAL TALK BOUNDARY.
  //
  // Two fences, both belonging to the provider and neither invented here (see
  // backend/talk_handoff.py, which owns every threshold; this surface holds no rule of its
  // own, only the numbers the server gave it):
  //
  //   Fence 1 — the provider's OWN reported context size, from `response.done.usage`,
  //             approaching the point where it starts silently dropping the beginning of the
  //             conversation. A calm, declinable, VISUAL offer. Never spoken, and never
  //             surfaced in the middle of a spoken exchange.
  //   Fence 2 — wall clock inside the 60-minute hard session cap, with a margin wide enough
  //             that her explanation and their decision cannot land on the cap. Visual, and
  //             she says it once, in her own words.
  //
  // Reaching a fence changes what is OFFERED. It never ends, shortens or interrupts the call.
  // ---------------------------------------------------------------------------------------
  const boundaryRef = useRef(null);            // server-issued thresholds
  const sessionOpenedAtRef = useRef(0);        // when the provider session was granted
  const reportedInputTokensRef = useRef(0);    // the provider's own last figure
  // The provider's own billing units, summed across this session and reported as a RUNNING TOTAL.
  // The server banks only the difference, so a retried or duplicated report cannot inflate it.
  const providerUsageRef = useRef({
    input_tokens: 0, output_tokens: 0, input_audio_tokens: 0, input_text_tokens: 0,
    input_cached_tokens: 0, output_audio_tokens: 0, output_text_tokens: 0,
  });
  const handoffDeclinedRef = useRef(false);
  const handoffRenotifiedRef = useRef(false);
  const handoffSpokenRef = useRef(false);
  const handoffBusyRef = useRef(false);
  const boundaryHushUntilRef = useRef(0);
  // A granted next Talk, waiting to be connected, and what governance said may travel with it.
  const prefetchedSessionRef = useRef(null);
  const pendingCarryRef = useRef(null);
  const [handoffOffer, setHandoffOffer] = useState(null);   // null | 'context' | 'boundary'
  const [handoffPhase, setHandoffPhase] = useState('idle'); // idle | moving | failed

  /**
   * Diagnostic telemetry helper - sends metadata to backend for iOS debugging
   * SECURITY: NO secrets, tokens, or audio are logged - metadata only
   */
  const sendVoiceDiagnostic = async (stage, errorCategory = null, errorMessage = null, metadata = {}) => {
    try {
      const duration = Date.now() - diagnosticStartTime.current;
      setDiagnosticStage(stage);
      
      await fetch(`${API_URL}/api/auth/diagnostic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_type: 'voice_connection',
          stage,
          error_category: errorCategory,
          error_message: errorMessage?.substring(0, 500),
          user_agent: navigator.userAgent,
          platform: navigator.platform,
          timestamp: new Date().toISOString(),
          metadata: {
            ...metadata,
            duration_ms: duration,
            talk_build: TALK_BUILD_STAMP,
            is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
            is_safari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
            has_webrtc: !!window.RTCPeerConnection,
            has_getUserMedia: !!navigator.mediaDevices?.getUserMedia,
            current_state: state,
            session_id: sessionIdRef.current?.substring(0, 20) // Safe prefix only
          }
        })
      });
    } catch (diagErr) {
      // Diagnostic send failed - log locally only
      console.error('[Voice Diagnostic] Failed to send:', diagErr.message);
    }
  };

  /**
   * Start transcript capture for this session
   */
  const startTranscript = async (sessionId, carry = null) => {
    try {
      const response = await fetchWithAuthRetry(`${API_URL}/api/realtime/transcript/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        // PHASE 7 BUNDLE 5 — the single-use handoff claim. The two Talks stay separate
        // transcripts, each with its own Keep / let-it-disappear decision; this only records
        // that the second continues the first, and only if this Talk owns the claim.
        body: JSON.stringify({
          session_id: sessionId,
          ...(carry?.token ? { handoff_token: carry.token, handoff_from: carry.from_session_id } : {})
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        transcriptIdRef.current = data.id;
        console.log('[Transcript] Started:', data.id);
      }
    } catch (err) {
      console.warn('[Transcript] Failed to start:', err);
    }
  };

  /**
   * Add a message to the transcript
   */
  const addToTranscript = async (role, content) => {
    if (!sessionIdRef.current || !content?.trim()) return;
    
    pendingTurnsRef.current.push({
      client_turn_id: newTranscriptTurnId(),
      session_id: sessionIdRef.current,
      role,
      content,
      // The clock of the moment it was spoken, not the moment it was finally accepted.
      occurred_at: new Date().toISOString(),
      language: lang
    });
    flushTranscriptQueue();
  };

  /**
   * Apply whatever the backend returned for a turn that is now durable.
   * Unchanged behaviour — only the call site moved into the flush loop.
   */
  const applyTranscriptTurnResult = (data) => {
    // Application-level safety reachability in Talk. The call is never interrupted; if the
    // backend recognises a crisis tier it returns the existing resources to show alongside it.
    if (data?.safety?.resources) {
      setSafetyNotice(data.safety.resources);
    }
    // 6C-11c (Founder ruling 1) — ACTIVE-SESSION MEMORY RESULT.
    // The Talk prompt is assembled once at session start, so a memory kept, corrected or let
    // go mid-call was invisible for the rest of that call — which is why she could keep
    // something and then truthfully say she had nothing on record. The backend returns the
    // authoritative result of the governed act; it is injected here as session context only.
    // No response is requested, so nothing extra is spoken: she simply knows it from now on.
    const addendum = data?.memory_result?.session_addendum;
    const dc = dataChannelRef.current;
    if (addendum && dc && dc.readyState === 'open') {
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [{ type: 'input_text', text: addendum }]
        }
      }));
    }
  };

  /**
   * A write could not be made durable. Hold the queue, tell the member, and try again.
   * The queue is never reordered and the failed turn is never skipped.
   */
  const scheduleTranscriptRetry = () => {
    setTranscriptDegraded(true);
    sendVoiceDiagnostic('transcript_write_deferred', 'transcript_write_failed', null, {
      queued_turns: pendingTurnsRef.current.length
    });
    if (transcriptRetryTimerRef.current) return;
    const delay = TRANSCRIPT_RETRY_DELAYS_MS[
      Math.min(transcriptRetryIndexRef.current, TRANSCRIPT_RETRY_DELAYS_MS.length - 1)
    ];
    transcriptRetryIndexRef.current += 1;
    transcriptRetryTimerRef.current = setTimeout(() => {
      transcriptRetryTimerRef.current = null;
      flushTranscriptQueue();
    }, delay);
  };

  /**
   * Drain the queue in the order the turns were spoken.
   *
   * The head is removed only once the backend has confirmed the turn is durable, so a failure
   * stops the drain with the queue intact rather than losing the turn. `fetchWithAuthRetry`
   * refreshes an expired cookie and retries once — the write is idempotent on `client_turn_id`,
   * so that retry cannot produce a second copy of one spoken turn.
   */
  const flushTranscriptQueue = async () => {
    if (flushingTranscriptRef.current) return;
    flushingTranscriptRef.current = true;
    try {
      while (pendingTurnsRef.current.length) {
        const turn = pendingTurnsRef.current[0];
        let data = null;
        let durable = false;
        try {
          const response = await fetchWithAuthRetry(
            `${API_URL}/api/realtime/transcript/message`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify(turn)
            }
          );
          durable = response.ok;
          if (durable) data = await response.json().catch(() => null);
          else console.warn('[Transcript] write not durable:', response.status);
        } catch (err) {
          console.warn('[Transcript] write failed:', err);
        }
        if (!durable) {
          scheduleTranscriptRetry();
          return;
        }
        pendingTurnsRef.current.shift();
        applyTranscriptTurnResult(data);
      }
      // Everything spoken so far is durable.
      if (transcriptRetryTimerRef.current) {
        clearTimeout(transcriptRetryTimerRef.current);
        transcriptRetryTimerRef.current = null;
      }
      transcriptRetryIndexRef.current = 0;
      setTranscriptDegraded(false);
    } finally {
      flushingTranscriptRef.current = false;
    }
  };

  // =========================================================================================
  // TALK METERING
  //
  // Three calls, all idempotent on the server:
  //   open  — WebRTC genuinely connected. Metering starts here and nowhere else.
  //   beat  — the call is still live. The server credits a CLAMPED delta; a replayed or
  //           out-of-order beat credits zero.
  //   close — a graceful end. If this never arrives (crash, closed lid, lost network) the
  //           server finalises the grant at the LAST VALID HEARTBEAT, never at "now".
  //
  // Every one of them fails silently. A metering fault must never shorten, interrupt or alter
  // a conversation.
  // =========================================================================================
  const stopMeterHeartbeat = () => {
    if (meterTimerRef.current) {
      clearInterval(meterTimerRef.current);
      meterTimerRef.current = null;
    }
  };

  const meterPost = async (path, payload) => {
    if (!talkGrantRef.current) return null;
    try {
      const res = await fetch(`${API_URL}/api/talk/meter/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ grant_id: talkGrantRef.current, ...payload })
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  };

  const closeMeter = async (reason = 'ended') => {
    stopMeterHeartbeat();
    if (!talkGrantRef.current || !meterOpenedRef.current) {
      talkGrantRef.current = null;
      return;
    }
    await meterPost('close', { reason, provider_usage: { ...providerUsageRef.current } });
    talkGrantRef.current = null;
    meterOpenedRef.current = false;
  };

  /**
   * The last thing we can send. A reload, a tab discard, a killed app or an unmount never runs
   * the async close path, so the closing delta would be lost. `keepalive` lets this one request
   * outlive the page. The server closes atomically, so if the normal close also lands, only one
   * of them banks anything.
   */
  const closeMeterBeacon = (reason) => {
    const grantId = talkGrantRef.current;
    if (!grantId || !meterOpenedRef.current) return;
    meterOpenedRef.current = false;
    talkGrantRef.current = null;
    stopMeterHeartbeat();
    try {
      fetch(`${API_URL}/api/talk/meter/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ grant_id: grantId, reason,
                               provider_usage: { ...providerUsageRef.current } }),
      });
    } catch (e) {
      // The server-side sweep is the backstop; it finalises at the last valid heartbeat.
    }
  };

  const sendMeterBeat = async () => {
    meterSeqRef.current += 1;
    const result = await meterPost('beat', { seq: meterSeqRef.current,
                                             provider_usage: { ...providerUsageRef.current } });
    if (!result || !isMountedRef.current) return;
    if (result.must_close && !meterMustCloseRef.current) {
      // The allowance is spent, including the bounded grace. Let the turn she is in the middle
      // of finish, then close through the NORMAL end path so the transcript still reaches the
      // member's own Keep / Let it disappear decision.
      meterMustCloseRef.current = true;
      setCapacityLimit(result.limit_message || null);
      const finish = () => {
        if (!isMountedRef.current) return;
        if (assistantAudibleRef.current) {
          setTimeout(finish, 500);
          return;
        }
        handleClose();
      };
      finish();
    }
  };

  const startMeterHeartbeat = async () => {
    if (!talkGrantRef.current || meterOpenedRef.current) return;
    meterOpenedRef.current = true;
    meterSeqRef.current = 0;
    meterMustCloseRef.current = false;
    await meterPost('open', {});
    stopMeterHeartbeat();
    meterTimerRef.current = setInterval(sendMeterBeat, meterIntervalMsRef.current);
  };

  /**
   * End transcript and get privacy info
   */
  const endTranscript = async () => {
    if (!sessionIdRef.current) {
      console.log('[Transcript] No session ID to end');
      return null;
    }
    
    console.log('[Transcript] Ending session:', sessionIdRef.current);
    
    // Anything still waiting must become durable BEFORE the transcript leaves ACTIVE, because
    // a turn can only be written to an ACTIVE transcript. `fetchWithAuthRetry` inside the flush
    // refreshes an expired cookie first, so the ordinary expired-session case drains here.
    await flushTranscriptQueue();
    if (pendingTurnsRef.current.length) {
      console.warn('[Transcript] ending with unsaved turns:', pendingTurnsRef.current.length);
      sendVoiceDiagnostic('transcript_unsaved_at_end', 'transcript_write_failed', null, {
        queued_turns: pendingTurnsRef.current.length
      });
    }
    
    try {
      const response = await fetchWithAuthRetry(`${API_URL}/api/realtime/transcript/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionIdRef.current })
      });
      
      console.log('[Transcript] End response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Transcript] Ended successfully:', {
          status: data.status,
          message_count: data.message_count,
          expires_at: data.expires_at
        });
        return data;
      } else {
        const errorText = await response.text();
        console.warn('[Transcript] End failed:', response.status, errorText);
      }
    } catch (err) {
      console.warn('[Transcript] Failed to end:', err);
    }
    return null;
  };

  /**
   * Save the transcript (user chose to keep it)
   */
  const saveTranscript = async () => {
    const targetId = transcriptIdRef.current;
    if (!targetId) return;
    
    setIsSubmittingDecision(true);
    try {
      await fetchWithAuthRetry(`${API_URL}/api/realtime/transcript/${targetId}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ decision: 'save' })
      });
      
      // Modal now handles its own closing with proper timing
    } catch (err) {
      console.error('[Transcript] Failed to save:', err);
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  /**
   * Let the transcript expire (user chose to let it disappear)
   */
  const letTranscriptExpire = async () => {
    const targetId = transcriptIdRef.current;
    if (!targetId) return;
    
    setIsSubmittingDecision(true);
    try {
      await fetchWithAuthRetry(`${API_URL}/api/realtime/transcript/${targetId}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ decision: 'expire' })
      });
      
      // Modal now handles its own closing with proper timing
    } catch (err) {
      console.error('[Transcript] Failed to let expire:', err);
    } finally {
      setIsSubmittingDecision(false);
    }
  };

  /**
   * TALK CONNECTION TRUTH — the connection deadline expired.
   *
   * Nothing here guesses WHY. The precise network cause is not proven, so the member is not
   * told their internet is at fault; they are told the one thing that is certainly true — Talk
   * could not connect — and offered a normal retry. Structural connection states are recorded
   * content-free so a real cause can be established later from evidence rather than assumption.
   */
  const failConnectionAttempt = async (attempt, cause) => {
    // The default cause is the connect deadline. A caller may pass a cause it has PROVEN
    // instead, so the record says what actually happened rather than blaming the clock.
    const reason = cause || {
      stage: 'connection_deadline_exceeded',
      category: 'connect_timeout',
      message: `data channel did not open within ${CONNECT_DEADLINE_MS}ms`,
      extra: { timeout: true, timeout_ms: CONNECT_DEADLINE_MS }
    };
    connectDeadlineRef.current = null;
    // An expired attempt may not speak for a newer one, and must never fire twice.
    if (attempt !== attemptIdRef.current || dataChannelOpenedRef.current) return;

    // Retire this attempt before anything else. A channel that opens after the deadline —
    // late DTLS, a slow relay, a provider that finally answers — must not be able to flip a
    // torn-down attempt into a connected-looking Talk that has no connection behind it.
    attemptIdRef.current = attempt + 1;
    const retired = attemptIdRef.current;

    const pc = peerConnectionRef.current;

    // Selected-pair existence and state only — never an address, never a candidate string.
    let selectedPair = null;
    try {
      if (pc?.getStats) {
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'candidate-pair' && (report.selected || report.nominated)) {
            selectedPair = { exists: true, state: report.state || null };
          }
        });
      }
    } catch (e) { /* stats unavailable on this platform — recorded as absent */ }

    await sendVoiceDiagnostic(reason.stage, reason.category, reason.message, {
        ...(reason.extra || {}),
        ice_gathering_state: pc?.iceGatheringState || null,
        ice_connection_state: pc?.iceConnectionState || null,
        peer_connection_state: pc?.connectionState || null,
        data_channel_state: dataChannelRef.current?.readyState || null,
        candidate_type_counts: { ...candidateTypeCountsRef.current },
        // N1 — the same question asked of the description and of Safari's own stats, so
        // "zero candidates" no longer rests on one event handler.
        sdp_candidate_evidence: sdpCandidateEvidence(pc?.localDescription?.sdp),
        stats_candidate_evidence: await statsCandidateEvidence(pc),
        selected_pair_exists: !!selectedPair,
        selected_pair_state: selectedPair?.state || null
      });

    // A connection that never opened leaves no conversation. End the transcript so no active or
    // recoverable Talk session is falsely recorded and nothing is offered back that never was.
    if (sessionIdRef.current) {
      try {
        await endTranscript();
      } catch (e) { /* the reclaim path is the backstop */ }
      sessionIdRef.current = null;
    }

    cleanupWebRTCResources();
    await sendVoiceDiagnostic('connection_timeout_cleanup_complete', null, null, {
      timeout: true,
      peer_connection_present: !!peerConnectionRef.current,
      data_channel_present: !!dataChannelRef.current,
      capture_tracks_present: !!localStreamRef.current
    });

    if (!isMountedRef.current || attemptIdRef.current !== retired) return;
    setError(lang === 'es'
      ? 'No se pudo conectar con Talk en este momento. Inténtalo de nuevo.'
      : "Talk couldn't connect right now. Please try again.");
    setState(STATE.ERROR);
  };

  /**
   * Initialize the WebRTC connection to Realtime API
   */
  const initializeConnection = async () => {
    // Prevent duplicate initialization
    if (isInitializingRef.current || peerConnectionRef.current) {
      console.log('[Realtime] Already initializing or connected, skipping');
      return;
    }
    isInitializingRef.current = true;
    diagnosticStartTime.current = Date.now();
    // One generation per member action. Every handler and continuation below belongs to this
    // attempt and checks that it is still the current one before it touches anything.
    const attempt = ++attemptIdRef.current;
    dataChannelOpenedRef.current = false;
    candidateTypeCountsRef.current = { host: 0, srflx: 0, relay: 0, other: 0 };
    
    // Send initial diagnostic
    await sendVoiceDiagnostic('voice_entry', null, null, { action: 'initializeConnection' });
    
    try {
      setState(STATE.CONNECTING);
      
      // Check for iOS Safari - may have WebRTC limitations
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isIOS) {
        console.log('[Realtime] iOS device detected - WebRTC may have limitations');
        await sendVoiceDiagnostic('ios_detected', null, null, { is_safari: isSafari });
      }
      
      // Step 1: Get ephemeral token from backend
      // Backend creates session with Arvexis's instructions and user context
      // Include credentials so backend can identify the user and build personalized context
      console.log('[Realtime] Requesting session from backend...');
      // Read-only: does the app already have location permission? This never prompts.
      locationGrantedRef.current = (await locationPermissionState()) === 'granted';
      await sendVoiceDiagnostic('session_request_start', null, null, { url: `${API_URL}/api/realtime/session`, location_permission: locationGrantedRef.current ? 'granted' : 'not_granted' });
      
      // PHASE 7 BUNDLE 5 — a handoff has already obtained this Talk from the provider before
      // the previous one was let go, so there is nothing left to ask for. Everything below
      // this point is the ordinary connect path, unchanged.
      const prefetched = prefetchedSessionRef.current;
      prefetchedSessionRef.current = null;

      const sessionResponse = prefetched ? null : await fetch(`${API_URL}/api/realtime/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies for auth
        body: JSON.stringify({
          timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return null; } })()
        })
      });
      
      if (sessionResponse) await sendVoiceDiagnostic('session_request_complete', null, null, { 
        status: sessionResponse.status,
        ok: sessionResponse.ok 
      });
      
      if (sessionResponse && !sessionResponse.ok) {
        const errorData = await sessionResponse.json().catch(() => ({}));
        await sendVoiceDiagnostic('session_request_failed', `http_${sessionResponse.status}`, errorData.detail);
        // Talk capacity for this period is spent. This is not a connection failure and there is
        // nothing to retry, so it is stated plainly and the session never opens — no partial
        // session, no failed connection for the member to interpret. Message stays available if
        // its own allowance remains, and nothing about the relationship has changed.
        if (sessionResponse.status === 402) {
          const detail = errorData.detail || {};
          if (isMountedRef.current) {
            setCapacityLimit(typeof detail === 'string' ? detail : (detail.message || null));
            setState(STATE.ERROR);
          }
          isInitializingRef.current = false;
          return;
        }
        if (sessionResponse.status === 403 && (errorData.detail || '').toLowerCase().includes('verif')) {
          throw new Error('Email verification required');
        }
        throw new Error(errorData.detail || 'Failed to create realtime session');
      }
      
      const sessionData = prefetched || await sessionResponse.json();
      const ephemeralToken = sessionData.client_secret?.value;
      // The real provider boundary this session lives inside, and the moment it was granted.
      // Both are needed before anything can be said about where the conversation stands.
      boundaryRef.current = sessionData.talk_boundary || null;
      sessionOpenedAtRef.current = Date.now();
      reportedInputTokensRef.current = 0;
      handoffDeclinedRef.current = false;
      handoffRenotifiedRef.current = false;
      handoffSpokenRef.current = false;
      boundaryHushUntilRef.current = 0;
      // A Talk that was PREFETCHED is the far side of a move in progress: the member is watching
      // "Moving with you…", and that must survive until the new Talk is actually live.
      if (!prefetched) {
        setHandoffOffer(null);
        setHandoffPhase('idle');
      }
      // The server's opaque metering identity for this Talk. Holding it consumes nothing:
      // metering only begins if WebRTC actually reaches 'connected'.
      talkGrantRef.current = sessionData.talk_grant_id || null;
      if (sessionData.talk_meter_interval_seconds) {
        meterIntervalMsRef.current = sessionData.talk_meter_interval_seconds * 1000;
      }
      meterOpenedRef.current = false;
      
      if (!ephemeralToken) {
        console.log('[Realtime] Session data:', sessionData);
        await sendVoiceDiagnostic('ephemeral_token_missing', 'invalid_response', 'No ephemeral token in response');
        throw new Error('No ephemeral token in session response');
      }
      
      await sendVoiceDiagnostic('ephemeral_token_received', null, null, { 
        model: sessionData.model,
        has_token: !!ephemeralToken 
      });
      
      console.log('[Realtime] Session created, got ephemeral token');
      
      // Generate a session ID for transcript tracking. A UUID where the platform provides one —
      // the old `Date.now()` + short random string was guessable, and the session id names the
      // transcript a turn is written into. The previous form remains the fallback for a WebView
      // without crypto.randomUUID, and existing ids keep working unchanged.
      const sessionId = newVoiceSessionId();
      sessionIdRef.current = sessionId;
      
      // Start transcript capture (Private by Default)
      await startTranscript(sessionId, pendingCarryRef.current);
      
      // Step 2: Create WebRTC peer connection
      await sendVoiceDiagnostic('rtc_peer_connection_create', null, null);
      const peerConnection = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      peerConnectionRef.current = peerConnection;
      await sendVoiceDiagnostic('rtc_peer_connection_created', null, null);
      
      // Step 3: Set up audio output element
      //
      // CLUSTER 3.5.5 — audio-route resilience.
      //
      // The element used to be created, never attached, and never watched. On iOS an audio
      // route change (headphones, AirPods, car) pauses media elements, and nothing here
      // noticed or resumed: her voice stopped for good while the session stayed connected
      // and visually present. `playsinline` and being in the document are what iOS expects
      // of a media element it should keep alive; `onpause` is what lets it come back.
      const audioElement = document.createElement('audio');
      audioElement.autoplay = true;
      audioElement.setAttribute('playsinline', '');
      audioElement.setAttribute('aria-hidden', 'true');
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);
      audioElement.onpause = () => {
        // Only a route change or an OS interruption pauses this element; the member has no
        // control over it. If the session is still live, resume.
        if (!isMountedRef.current || !audioElement.srcObject) return;
        sendVoiceDiagnostic('remote_audio_paused', null, null, {
          connection: peerConnectionRef.current?.connectionState || null
        });
        audioElement.play().catch(() => {
          sendVoiceDiagnostic('remote_audio_resume_failed', null, null);
        });
      };
      audioElementRef.current = audioElement;
      
      // Handle incoming audio track (Arvexis's voice)
      peerConnection.ontrack = (event) => {
        if (attempt !== attemptIdRef.current) return;
        console.log('[Realtime] Received audio track');
        sendVoiceDiagnostic('remote_audio_track_received', null, null);
        const stream = event.streams[0];
        audioElement.srcObject = stream;
        
        // Store remote stream for audio analysis (orb reactivity)
        if (isMountedRef.current) {
          setRemoteStream(stream);
        }
      };
      
      // ICE candidate TYPE counts, for connection evidence at the deadline. Type only — the
      // candidate string, its address and its port are never read and never recorded.
      peerConnection.onicecandidate = (event) => {
        const type = event.candidate?.type;
        if (!type) return;
        const counts = candidateTypeCountsRef.current;
        if (type in counts) counts[type] += 1;
        else counts.other += 1;
      };

      // Connection state monitoring with diagnostics
      peerConnection.onconnectionstatechange = () => {
        if (attempt !== attemptIdRef.current) return;
        const connState = peerConnection.connectionState;
        console.log('[Realtime] Connection state:', connState);
        sendVoiceDiagnostic('peer_connection_state_change', null, null, { state: connState });
        // METERING BEGINS HERE AND NOWHERE ELSE. A minted token, an SDP exchange or an attempt
        // that never connected is not a conversation and is never charged.
        if (connState === 'connected') {
          startMeterHeartbeat();
        }
        if (connState === 'failed') {
          sendVoiceDiagnostic('peer_connection_failed', 'connection_failed', 'RTCPeerConnection state failed');
          // The transport is gone. Bank what was actually spoken and close the grant as
          // interrupted, rather than leaving the closing delta to be dropped by the sweep.
          closeMeter('interrupted');
          setError('Connection failed');
          setState(STATE.ERROR);
        }
        // CLUSTER 3.5.5 — 'disconnected' used to be logged and ignored, so a transport that
        // never recovered left Talk sitting there looking present with nothing arriving.
        // Transient dips are normal, so give it room, then say so honestly.
        if (connState === 'disconnected') {
          if (disconnectGraceRef.current) clearTimeout(disconnectGraceRef.current);
          disconnectGraceRef.current = setTimeout(() => {
            disconnectGraceRef.current = null;
            if (!isMountedRef.current) return;
            if (peerConnectionRef.current?.connectionState === 'disconnected') {
              sendVoiceDiagnostic('peer_connection_disconnected_persisted', 'connection_lost',
                'RTCPeerConnection stayed disconnected');
              closeMeter('interrupted');
              setError('Connection lost');
              setState(STATE.ERROR);
            }
          }, DISCONNECT_GRACE_MS);
        } else if (disconnectGraceRef.current) {
          clearTimeout(disconnectGraceRef.current);
          disconnectGraceRef.current = null;
        }
      };
      
      // ICE connection state monitoring with diagnostics
      peerConnection.oniceconnectionstatechange = () => {
        if (attempt !== attemptIdRef.current) return;
        const iceState = peerConnection.iceConnectionState;
        console.log('[Realtime] ICE connection state:', iceState);
        sendVoiceDiagnostic('ice_connection_state_change', null, null, { state: iceState });
      };
      
      // ICE gathering state monitoring
      peerConnection.onicegatheringstatechange = () => {
        const gatherState = peerConnection.iceGatheringState;
        console.log('[Realtime] ICE gathering state:', gatherState);
        sendVoiceDiagnostic('ice_gathering_state_change', null, null, { state: gatherState });
      };
      
      // Step 4: Get microphone access and add to connection
      // iOS Safari requires user gesture and may prompt for permission
      console.log('[Realtime] Requesting microphone access...');
      await sendVoiceDiagnostic('microphone_request_start', null, null);
      
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } 
        });
        await sendVoiceDiagnostic('microphone_acquired', null, null, { 
          track_count: stream.getTracks().length,
          // Confirms what the platform ACTUALLY applied, not what we asked for: on some iOS
          // routes acoustic echo cancellation is silently unavailable, which is exactly the
          // condition under which her own voice can return as a member turn.
          ...(() => {
            const s = stream.getAudioTracks()[0]?.getSettings?.() || {};
            return {
              echo_cancellation: s.echoCancellation ?? null,
              noise_suppression: s.noiseSuppression ?? null,
              auto_gain_control: s.autoGainControl ?? null
            };
          })()
        });
        console.log('[Realtime] Microphone access granted');
      } catch (micError) {
        console.error('[Realtime] Microphone error:', micError);
        await sendVoiceDiagnostic('microphone_denied', 'permission_denied', micError.message);
        throw new Error('Microphone access needed');
      }
      // The user can leave Talk while the permission prompt or setup is still in
      // flight. Unmount cleanup has already run by then, so an unguarded stream
      // would stay live with nothing left to close it.
      if (!isMountedRef.current) {
        console.log('[Realtime] Left Talk during mic setup — stopping capture');
        stream.getTracks().forEach(track => track.stop());
        try { peerConnection.close(); } catch (e) { /* already closed */ }
        return;
      }
      localStreamRef.current = stream;
      // The orb's listening response reads the member's own capture, but nothing is attached to
      // this stream until the data channel opens: on iOS Safari a Web Audio node mounted on a
      // live sender track during ICE gathering stopped candidates being gathered at all.
      
      stream.getTracks().forEach(track => {
        peerConnection.addTrack(track, stream);
      });
      await sendVoiceDiagnostic('local_track_added', null, null);

      // CLUSTER 3.5.5 — watch the microphone, not just the connection.
      //
      // On an audio route change iOS can mute or end the capture track while the sender,
      // the connection state and the orb all stay exactly as they were. Nothing observed
      // that, so Talk looked present and heard nothing, with no way back: getUserMedia was
      // called once and never again. Mute is reported; an ended track is re-acquired once
      // and swapped into the existing sender, which keeps the conversation rather than
      // restarting it.
      const watchCaptureTrack = (t) => {
        t.onmute = () => sendVoiceDiagnostic('local_track_muted', null, null, {
          connection: peerConnectionRef.current?.connectionState || null
        });
        t.onunmute = () => sendVoiceDiagnostic('local_track_unmuted', null, null);
        t.onended = async () => {
          sendVoiceDiagnostic('local_track_ended', null, null, {
            reacquired: micReacquiredRef.current
          });
          if (!isMountedRef.current || micReacquiredRef.current) return;
          const pc = peerConnectionRef.current;
          if (!pc || pc.connectionState === 'closed' || pc.connectionState === 'failed') return;
          micReacquiredRef.current = true;
          try {
            const fresh = await navigator.mediaDevices.getUserMedia({
              audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            });
            if (!isMountedRef.current) {
              fresh.getTracks().forEach(x => x.stop());
              return;
            }
            const freshTrack = fresh.getAudioTracks()[0];
            const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
            if (sender && freshTrack) {
              await sender.replaceTrack(freshTrack);
              localStreamRef.current = fresh;
              setLocalStream(fresh);
              watchCaptureTrack(freshTrack);
              sendVoiceDiagnostic('microphone_reacquired', null, null);
            } else {
              fresh.getTracks().forEach(x => x.stop());
            }
          } catch (e) {
            sendVoiceDiagnostic('microphone_reacquire_failed', 'capture_lost', e?.message);
          }
        };
      };
      stream.getAudioTracks().forEach(watchCaptureTrack);

      // An audio route change is recorded but never treated as a reason to end the session:
      // where Arvexis is heard changing is not the session ending. Playback is nudged back
      // in case iOS paused the element on the way through.
      if (navigator.mediaDevices && 'ondevicechange' in navigator.mediaDevices) {
        const onDeviceChange = () => {
          if (!isMountedRef.current) return;
          sendVoiceDiagnostic('audio_route_changed', null, null, {
            connection: peerConnectionRef.current?.connectionState || null,
            capture_state: localStreamRef.current?.getAudioTracks()?.[0]?.readyState || null,
            capture_muted: localStreamRef.current?.getAudioTracks()?.[0]?.muted ?? null
          });
          const el = audioElementRef.current;
          if (el && el.srcObject && el.paused) el.play().catch(() => {});
        };
        navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
        deviceChangeHandlerRef.current = onDeviceChange;
      }
      
      // Step 5: Set up data channel for events
      const dataChannel = peerConnection.createDataChannel('oai-events');
      dataChannelRef.current = dataChannel;
      await sendVoiceDiagnostic('data_channel_created', null, null);

      // The transport phase begins here, and so does the only deadline. If the channel has not
      // opened by then, Talk stops claiming to connect and says so.
      if (connectDeadlineRef.current) clearTimeout(connectDeadlineRef.current);
      connectDeadlineRef.current = setTimeout(() => {
        failConnectionAttempt(attempt);
      }, CONNECT_DEADLINE_MS);

      dataChannel.onopen = () => {
        if (attempt !== attemptIdRef.current) return;
        console.log('[Realtime] Data channel opened - connection established');
        dataChannelOpenedRef.current = true;
        if (connectDeadlineRef.current) {
          clearTimeout(connectDeadlineRef.current);
          connectDeadlineRef.current = null;
        }
        sendVoiceDiagnostic('data_channel_open', null, null);
        // The only point at which a real WebRTC connection provably exists.
        sendVoiceDiagnostic('webrtc_established', null, null, {
          ice_connection_state: peerConnection.iceConnectionState,
          peer_connection_state: peerConnection.connectionState
        });
        if (isMountedRef.current) {
          sendVoiceDiagnostic('connected_success', null, null);
          setState(STATE.CONNECTED);
          // Only now does the orb begin reading the member's capture. Same stream, one analyser.
          setLocalStream(localStreamRef.current);
        }
        // PHASE 7 BUNDLE 5 — if this Talk continues another one, the governed working tail
        // goes in now, and she resumes from it. Consumed exactly once.
        const carry = pendingCarryRef.current;
        pendingCarryRef.current = null;
        if (carry) {
          injectCarriedContext(carry);
          // The move is complete. The orb, not a panel, has been carrying it.
          if (isMountedRef.current) {
            setHandoffOffer(null);
            setHandoffPhase('idle');
          }
        }
      };
      
      dataChannel.onmessage = (event) => {
        if (attempt !== attemptIdRef.current) return;
        handleRealtimeEvent(JSON.parse(event.data));
      };
      
      dataChannel.onerror = (error) => {
        console.error('[Realtime] Data channel error:', error);
        sendVoiceDiagnostic('data_channel_error', 'data_channel_error', error?.message || 'unknown');
      };
      
      // Step 6: Create and send offer
      await sendVoiceDiagnostic('sdp_offer_create_start', null, null);
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      await sendVoiceDiagnostic('sdp_offer_created', null, null, { 
        sdp_type: offer.type,
        sdp_length: offer.sdp?.length 
      });

      // C2 — wait for ICE gathering before the non-trickle POST. Bounded by
      // ICE_GATHERING_BUDGET_MS, which sits inside the unchanged CONNECT_DEADLINE_MS.
      const waitForGathering = (budgetMs) => {
        if (peerConnection.iceGatheringState === 'complete') return Promise.resolve();
        return new Promise((resolve) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            clearTimeout(budgetTimer);
            peerConnection.removeEventListener('icegatheringstatechange', onGatheringChange);
            resolve();
          };
          const onGatheringChange = () => {
            if (peerConnection.iceGatheringState === 'complete') finish();
          };
          const budgetTimer = setTimeout(finish, budgetMs);
          peerConnection.addEventListener('icegatheringstatechange', onGatheringChange);
          if (peerConnection.iceGatheringState === 'complete') finish();
        });
      };
      const candidatesGathered = () =>
        Object.values(candidateTypeCountsRef.current).reduce((total, n) => total + n, 0);

      const gatheringStartedAt = Date.now();
      await waitForGathering(ICE_GATHERING_BUDGET_MS);
      if (attempt !== attemptIdRef.current) return;
      await sendVoiceDiagnostic('ice_gathering_wait_complete', null, null, {
        ice_gathering_state: peerConnection.iceGatheringState,
        waited_ms: Date.now() - gatheringStartedAt,
        budget_ms: ICE_GATHERING_BUDGET_MS,
        candidate_type_counts: { ...candidateTypeCountsRef.current },
        sdp_candidate_evidence: sdpCandidateEvidence(peerConnection.localDescription?.sdp),
        stats_candidate_evidence: await statsCandidateEvidence(peerConnection),
      });

      // C3 — zero candidates of any type. Restart ICE once and give the agent a shorter,
      // bounded second chance before anything is posted or spent.
      if (candidatesGathered() === 0) {
        await sendVoiceDiagnostic('ice_gathering_produced_no_candidates', null, null, {
          ice_gathering_state: peerConnection.iceGatheringState,
          waited_ms: Date.now() - gatheringStartedAt,
          candidate_type_counts: { ...candidateTypeCountsRef.current },
        });
        const regatherStartedAt = Date.now();
        try {
          const restartOffer = await peerConnection.createOffer({ iceRestart: true });
          await peerConnection.setLocalDescription(restartOffer);
        } catch (restartError) {
          await sendVoiceDiagnostic('ice_restart_failed', 'ice_restart_failed',
            restartError?.message || 'ICE restart rejected');
        }
        if (attempt !== attemptIdRef.current) return;
        await waitForGathering(ICE_REGATHER_BUDGET_MS);
        if (attempt !== attemptIdRef.current) return;
        await sendVoiceDiagnostic('ice_regather_complete', null, null, {
          ice_gathering_state: peerConnection.iceGatheringState,
          waited_ms: Date.now() - regatherStartedAt,
          budget_ms: ICE_REGATHER_BUDGET_MS,
          candidate_type_counts: { ...candidateTypeCountsRef.current },
          sdp_candidate_evidence: sdpCandidateEvidence(peerConnection.localDescription?.sdp),
          stats_candidate_evidence: await statsCandidateEvidence(peerConnection),
        });

        // Still nothing. The offer cannot carry a transport, so it is not sent: no provider
        // call is spent on an attempt that cannot succeed, and the member is told now rather
        // than after a deadline that would only confirm what is already known.
        if (candidatesGathered() === 0) {
          await failConnectionAttempt(attempt, {
            stage: 'ice_gathering_unavailable',
            category: 'no_ice_candidates',
            message: 'no ICE candidates of any type were gathered after one ICE restart; '
                   + 'the offer cannot carry a transport',
            extra: {
              timeout: false,
              regathered: true,
              regather_budget_ms: ICE_REGATHER_BUDGET_MS,
              offer_posted: false,
            },
          });
          return;
        }
      }

      // The gathered description, not the pre-gathering offer. This is the line that makes the
      // wait above mean anything.
      const offerSdp = peerConnection.localDescription?.sdp || offer.sdp;
      
      // Step 7: Send offer to OpenAI with ephemeral token
      // GA ephemeral-token contract: raw SDP body with Content-Type application/sdp.
      // (A multipart Blob part is rejected: 'field "sdp" is required but not found')
      console.log('[Realtime] Sending SDP offer to OpenAI (application/sdp)...');
      await sendVoiceDiagnostic('openai_call_start', null, null, { 
        url: 'https://api.openai.com/v1/realtime/calls',
        content_type: 'application/sdp',
        sdp_length: offerSdp?.length
      });
      
      const negotiateResponse = await fetch(
        'https://api.openai.com/v1/realtime/calls',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${ephemeralToken}`,
            'Content-Type': 'application/sdp'
          },
          body: offerSdp
        }
      );
      
      await sendVoiceDiagnostic('openai_call_response', null, null, { 
        status: negotiateResponse.status,
        ok: negotiateResponse.ok 
      });
      
      if (!negotiateResponse.ok) {
        const errorText = await negotiateResponse.text();
        const status = negotiateResponse.status;
        console.error(`[Realtime] Negotiation failed - HTTP ${status}:`, errorText);
        
        // Parse error for user-friendly message and diagnostics
        let diagnosticMessage = `HTTP ${status}`;
        let errorData = null;
        try {
          errorData = JSON.parse(errorText);
          diagnosticMessage = errorData.error?.message || errorData.detail || errorText;
          console.error('[Realtime] OpenAI error details:', errorData);
          
          await sendVoiceDiagnostic('openai_call_failed', `http_${status}`, diagnosticMessage?.substring(0, 200), {
            error_code: errorData.error?.code,
            error_type: errorData.error?.type
          });
        } catch (parseErr) {
          // Not JSON, log raw response
          console.error('[Realtime] Raw error response:', errorText.substring(0, 500));
          await sendVoiceDiagnostic('openai_call_failed', `http_${status}`, errorText?.substring(0, 200));
        }
        // Specific conditions must be thrown OUTSIDE the parse try/catch or they get swallowed
        if (errorData?.error?.message?.includes('quota') || errorData?.error?.type === 'insufficient_quota') {
          throw new Error('Service temporarily unavailable');
        }
        if (errorData?.error?.code === 'invalid_api_key') {
          throw new Error('Voice service configuration error');
        }
        throw new Error(`WebRTC negotiation failed: ${diagnosticMessage}`);
      }
      
      const answerSdp = await negotiateResponse.text();
      
      // Same race one step later: the session must not come up behind a screen
      // the user has already left.
      if (!isMountedRef.current) {
        console.log('[Realtime] Left Talk during negotiation — tearing session down');
        cleanupWebRTCResources();
        return;
      }
      console.log('[Realtime] Received SDP answer from OpenAI');
      await sendVoiceDiagnostic('sdp_answer_received', null, null, { 
        sdp_length: answerSdp?.length 
      });
      
      await sendVoiceDiagnostic('set_remote_description_start', null, null);
      await peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp
      });
      await sendVoiceDiagnostic('set_remote_description_complete', null, null);

      // TELEMETRY TRUTH: applying the answer is not a connection. This event used to be
      // 'webrtc_established' and was emitted here — on one measured attempt it was recorded
      // BEFORE ice_connection_state=checking, asserting a connection that did not exist and
      // never would. It now reports only the stage it truthfully proves; 'webrtc_established'
      // is emitted at data-channel open, where connectivity is real.
      console.log('[Realtime] SDP answer applied — awaiting real connectivity');
      await sendVoiceDiagnostic('remote_description_set', null, null);
      
    } catch (err) {
      console.error('[Realtime] Connection error:', err);
      if (connectDeadlineRef.current) {
        clearTimeout(connectDeadlineRef.current);
        connectDeadlineRef.current = null;
      }
      await sendVoiceDiagnostic('connection_error', 'exception', err.message?.substring(0, 300));
      isInitializingRef.current = false; // Reset on error
      if (isMountedRef.current && attempt === attemptIdRef.current) {
        // User-friendly error messages with iOS hint
        let userMessage = 'Connection failed';
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        if (err.message.includes('Email verification required')) {
          userMessage = lang === 'es' ? 'Verifica tu correo para usar la voz' : 'Verify your email to use voice';
        } else if (err.message.includes('unavailable') || err.message.includes('quota')) {
          userMessage = 'Voice is temporarily unavailable';
        } else if (err.message.includes('microphone') || err.message.includes('Permission') || err.message.includes('NotAllowed')) {
          userMessage = 'Microphone access needed';
        } else if (err.message.includes('WebRTC') || err.message.includes('network')) {
          userMessage = isIOS ? 'Connection issue - try again' : 'Connection failed';
        }
        
        console.log('[Realtime] Setting error state:', userMessage);
        setError(userMessage);
        setState(STATE.ERROR);
      }
    }
  };

  /**
   * Handle events from the Realtime API
   */
  // Live lookup + dream recall tool calls → backend → function_call_output → new response
  const handleFunctionCall = async (event) => {
    const TOOL_ENDPOINTS = {
      lookup_current_information: '/api/realtime/tool/lookup',
      recall_member_dream: '/api/realtime/tool/dream_recall',
      recall_repeated_life: '/api/realtime/tool/life_recurrence',
      calculate: '/api/realtime/tool/calculate',
      recall_earlier_conversation: '/api/realtime/tool/conversation_recall',
      founder_product_intelligence: '/api/realtime/tool/founder_intelligence',
    };
    // 4.3 — one caption per invisible operation, from the shared vocabulary. The key is chosen
    // by the tool that is actually about to run; no tool, no caption.
    const TOOL_ACTIVITY = {
      lookup_current_information: ACTIVITY.SEARCHING,
      recall_member_dream: ACTIVITY.RECALLING_DREAM,
      recall_repeated_life: ACTIVITY.CONNECTING,
      calculate: ACTIVITY.THINKING,
      recall_earlier_conversation: ACTIVITY.CONNECTING,
      founder_product_intelligence: ACTIVITY.THINKING,
    };
    const endpoint = TOOL_ENDPOINTS[event.name];
    if (!endpoint) return;
    let args = {};
    try { args = JSON.parse(event.arguments || '{}'); } catch (e) { args = {}; }
    const query = (args.query || '').toString().slice(0, 500);
    // The Founder Intelligence tool names which readings it needs; every other tool ignores this.
    const topics = Array.isArray(args.topics)
      ? args.topics.slice(0, 6).map((t) => String(t).slice(0, 40))
      : null;
    await sendVoiceDiagnostic('tool_call_received', null, null, { tool: event.name, query_length: query.length });
    let output;
    setTalkActivity(TOOL_ACTIVITY[event.name] || null);
    try {
      const callLookup = async (extra = {}) => {
        const res = await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ query, session_id: sessionIdRef.current || undefined,
                                 timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                 ...(topics ? { topics } : {}),
                                 ...extra })
        });
        return { res, body: res.ok ? await res.json() : null };
      };
      // Already granted → get a fresh fix now, silently, before the lookup runs. No prompt,
      // no dialog mid-conversation, and no dependence on the model asking for it.
      let preFix = null;
      if (locationGrantedRef.current) {
        const got = await requestDeviceLocation();
        if (got.location) preFix = got;
      }
      let { res, body } = await callLookup(
        preFix ? { location: preFix.location, location_permission: preFix.permission } : {}
      );
      // 4.1 — the backend says this needs to know where they are. Ask the device (the browser
      // prompts the first time), then run the SAME lookup once more with whatever came back.
      // Granted → she answers for where they actually are. Denied → she asks for a city. The
      // fix is used for this one request and never kept.
      if (body && body.location_needed) {
        const fix = await requestDeviceLocation();
        // A contextual grant is app-wide from here on: later turns acquire silently.
        if (fix.permission === 'granted') locationGrantedRef.current = true;
        ({ res, body } = await callLookup({ location: fix.location || undefined,
                                            location_permission: fix.permission }));
      }
      output = body || { success: false, found: false, facts: [], note: `Unavailable (HTTP ${res.status}); be honest about it.` };
      await sendVoiceDiagnostic('tool_lookup_complete', null, null, { tool: event.name, status: res.status, success: !!(output.success || output.found), facts: (output.facts || []).length, confidence: output.confidence });
    } catch (err) {
      output = { success: false, found: false, facts: [], note: 'Lookup failed; be honest about it.' };
      await sendVoiceDiagnostic('tool_lookup_failed', 'exception', err.message, { tool: event.name });
    } finally {
      // The operation is over either way. On failure the caption simply disappears — Arvexis
      // says what happened in her own words rather than a second system voice announcing it.
      setTalkActivity(null);
    }
    const dc = dataChannelRef.current;
    if (!dc || dc.readyState !== 'open') return;
    dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: event.call_id, output: JSON.stringify(output) }
    }));
    dc.send(JSON.stringify({ type: 'response.create' }));
  };

  const handleRealtimeEvent = (event) => {
    console.log('[Realtime] Event:', event.type);
    
    switch (event.type) {
      case 'session.created':
        console.log('[Realtime] Session created');
        break;
        
      case 'input_audio_buffer.speech_started':
        // User started speaking
        // A segment captured while Arvexis is audible may be her own voice returning, so the
        // overlap is recorded HERE — by the time the transcription arrives, playback has
        // usually finished and the evidence would be gone.
        memberTurnRef.current = {
          startedAt: Date.now(),
          overlappedPlayback: assistantAudibleRef.current
            || (Date.now() - assistantAudioStoppedAtRef.current) < PLAYBACK_OVERLAP_GRACE_MS
        };
        if (isMountedRef.current) {
          setState(STATE.LISTENING);
          setIsArvexisSpeaking(false);
        }
        break;
        
      case 'input_audio_buffer.speech_stopped':
        // User stopped speaking — Arvexis is now thinking
        console.log('[Realtime] User speech stopped, Arvexis thinking...');
        if (memberTurnRef.current) {
          memberTurnRef.current.stoppedAt = Date.now();
          sendVoiceDiagnostic('turn_commit', null, null, {
            turn_ms: memberTurnRef.current.stoppedAt - memberTurnRef.current.startedAt,
            playback_overlap: memberTurnRef.current.overlappedPlayback
          });
        }
        if (isMountedRef.current) {
          setState(STATE.THINKING);
        }
        break;
        
      case 'response.audio.started':
      case 'response.created':
        // Arvexis started responding
        assistantAudibleRef.current = true;
        if (isMountedRef.current) {
          setState(STATE.SPEAKING);
          setIsArvexisSpeaking(true);
        }
        break;
        
      case 'response.audio.done':
      case 'response.output_audio.done':
      case 'response.done':
        // Arvexis finished responding
        assistantAudibleRef.current = false;
        assistantAudioStoppedAtRef.current = Date.now();
        // PHASE 7 BUNDLE 5 — the provider's OWN report of how much context it just used. This
        // is the only truthful measure of where the conversation stands; nothing is estimated,
        // and nothing derived from it is ever shown to the member.
        if (event.type === 'response.done') {
          const reported = event.response?.usage?.input_tokens;
          if (typeof reported === 'number' && reported > reportedInputTokensRef.current) {
            reportedInputTokensRef.current = reported;
          }
          // Founder ruling 2026-10-01 — the provider's OWN billing units, summed across the
          // session so provider cost can be reconciled later from measured units rather than
          // estimated from wall clock. Integers only; no content of any kind.
          const u = event.response?.usage;
          if (u) {
            const n = (v) => (typeof v === 'number' && v > 0 ? v : 0);
            const t = providerUsageRef.current;
            t.input_tokens += n(u.input_tokens);
            t.output_tokens += n(u.output_tokens);
            t.input_audio_tokens += n(u.input_token_details?.audio_tokens);
            t.input_text_tokens += n(u.input_token_details?.text_tokens);
            t.input_cached_tokens += n(u.input_token_details?.cached_tokens);
            t.output_audio_tokens += n(u.output_token_details?.audio_tokens);
            t.output_text_tokens += n(u.output_token_details?.text_tokens);
          }
        }
        if (isMountedRef.current) {
          setState(STATE.CONNECTED);
          setIsArvexisSpeaking(false);
          // A calm return rather than a snap back to rest.
          setSettlingUntil(Date.now() + ORB_SETTLE_MS);
        }
        break;
      
      case 'conversation.item.input_audio_transcription.completed': {
        // User's speech was transcribed — but only the member's own words may be stored as
        // the member's words. A content-free committed segment still produces text, and her
        // voice can leak back into the microphone; neither is the member speaking.
        const turn = memberTurnRef.current;
        const verdict = evaluateMemberTranscript({
          text: event.transcript,
          assistantTexts: recentAssistantTextRef.current,
          overlappedPlayback: !!turn?.overlappedPlayback,
          latinScriptSession: lang === 'en' || lang === 'es'
        });
        // Metadata only — no transcript text ever leaves in a diagnostic.
        sendVoiceDiagnostic(
          verdict.accept ? 'turn_transcribed' : 'turn_refused',
          verdict.accept ? null : verdict.reason,
          null,
          {
            chars: (event.transcript || '').length,
            playback_overlap: !!turn?.overlappedPlayback,
            echo_score: verdict.score,
            turn_ms: turn?.stoppedAt && turn?.startedAt ? turn.stoppedAt - turn.startedAt : null
          }
        );
        memberTurnRef.current = null;
        // Lane 3 — when her own voice reached the microphone, server VAD has already committed
        // that segment and already begun a reply to nothing. Refusing the transcript leaves the
        // reply standing, which is the extra turn members hear; that reply is cancelled and the
        // phantom item is removed from the session. Only a measured self-utterance verdict does
        // this, so real speech over her playback still interrupts and still gets answered.
        const corrections = selfEchoCorrections(verdict, event.item_id);
        if (corrections.length) {
          const dc = dataChannelRef.current;
          if (dc && dc.readyState === 'open') {
            corrections.forEach((correction) => dc.send(JSON.stringify(correction)));
          }
        }
        if (verdict.accept && event.transcript) {
          addToTranscript('user', event.transcript);
          // The member's own words decide whether a turn needs their location — never the query
          // the model writes for the lookup tool.
          lastMemberTextRef.current = event.transcript;
        }
        break;
      }

      case 'conversation.item.input_audio_transcription.failed':
        // The engine could not transcribe the segment. Nothing is stored: a failure is not
        // a member utterance.
        sendVoiceDiagnostic('turn_transcription_failed', 'transcription_failed', null, {
          playback_overlap: !!memberTurnRef.current?.overlappedPlayback
        });
        memberTurnRef.current = null;
        break;
        
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done': // GA event name (session type "realtime")
        // Arvexis's response was transcribed — capture for transcript
        if (event.transcript) {
          // Kept only long enough to recognise her own voice coming back on the microphone.
          recentAssistantTextRef.current = [event.transcript, ...recentAssistantTextRef.current].slice(0, 3);
          addToTranscript('arvexis', event.transcript);
        }
        break;
        
      case 'response.function_call_arguments.done':
        // Live lookup tool: same retrieval pipeline as Chat, executed via backend
        handleFunctionCall(event);
        break;
        
      case 'error':
        console.error('[Realtime] API error:', event.error);
        break;
        
      default:
        // Other events (transcripts, etc.)
        break;
    }
  };

  /**
   * Clean up all WebRTC resources without triggering close flow
   * Used for retry and error recovery
   */
  const cleanupWebRTCResources = () => {
    console.log('[Realtime] Cleaning up WebRTC resources for retry...');

    // The meter is session-scoped. Stop beating immediately; a grant that is never closed is
    // finalised server-side at its last valid heartbeat, so nothing dead air is ever charged.
    stopMeterHeartbeat();

    // Audio-route watchers (3.5.5) must not outlive the session that installed them
    if (deviceChangeHandlerRef.current) {
      try {
        navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandlerRef.current);
      } catch (e) { /* nothing to remove */ }
      deviceChangeHandlerRef.current = null;
    }
    if (disconnectGraceRef.current) {
      clearTimeout(disconnectGraceRef.current);
      disconnectGraceRef.current = null;
    }
    // The connection deadline is attempt-scoped and must not outlive the attempt that armed it.
    if (connectDeadlineRef.current) {
      clearTimeout(connectDeadlineRef.current);
      connectDeadlineRef.current = null;
    }
    micReacquiredRef.current = false;
    
    // Stop local media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.onmute = null;
        track.onunmute = null;
        track.onended = null;
        track.stop();
        console.log('[Realtime] Stopped track:', track.kind);
      });
      localStreamRef.current = null;
    }
    
    // Close data channel
    if (dataChannelRef.current) {
      try {
        dataChannelRef.current.close();
      } catch (e) {
        console.warn('[Realtime] Data channel close error:', e);
      }
      dataChannelRef.current = null;
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      try {
        peerConnectionRef.current.close();
      } catch (e) {
        console.warn('[Realtime] Peer connection close error:', e);
      }
      peerConnectionRef.current = null;
    }
    
    // Clear audio element
    if (audioElementRef.current) {
      audioElementRef.current.onpause = null;
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      if (audioElementRef.current.parentNode) {
        audioElementRef.current.parentNode.removeChild(audioElementRef.current);
      }
      audioElementRef.current = null;
    }
    
    // Clear remote stream state
    setRemoteStream(null);
    setLocalStream(null);
    
    // Reset initialization flag
    isInitializingRef.current = false;
    
    console.log('[Realtime] WebRTC cleanup complete');
  };

  // =========================================================================================
  // PHASE 7 BUNDLE 5 — TALK CONTINUITY AT THE REAL BOUNDARY.
  //
  // Nothing in here ends a conversation. A fence changes what is offered; the member decides.
  // =========================================================================================

  /**
   * Place the governed working tail into the Talk that has just opened, then let her resume.
   *
   * The turns are real conversation items with their real speakers, not a summary and not a
   * system assertion: the member said what they said, she said what she said. The note that
   * follows them is what stops a carried turn being mistaken for something newly established,
   * and the arrival contract in the session instructions is what stops her announcing a
   * transition. `response.create` is what makes her speak first, in the register they were
   * already in — the Talk equivalent of the approved Message arrival.
   */
  const injectCarriedContext = (carry) => {
    const dc = dataChannelRef.current;
    if (!carry || !dc || dc.readyState !== 'open') return;
    try {
      (carry.carried || []).forEach((turn) => {
        const assistant = turn.role === 'assistant';
        dc.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: assistant ? 'assistant' : 'user',
            content: [{ type: assistant ? 'output_text' : 'input_text', text: turn.content }]
          }
        }));
      });
      if (carry.context_note) {
        dc.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'system',
            content: [{ type: 'input_text', text: carry.context_note }]
          }
        }));
      }
      dc.send(JSON.stringify({ type: 'response.create' }));
      console.log('[TalkHandoff] carried context placed:', (carry.carried || []).length);
      sendVoiceDiagnostic('talk_handoff_arrival', null, null, {
        carried_turns: (carry.carried || []).length
      });
    } catch (err) {
      // A failed injection leaves a clean Talk with her governed snapshot — today's behaviour.
      sendVoiceDiagnostic('talk_handoff_arrival_failed', 'injection_failed', err?.message || null);
    }
  };

  /**
   * Fence 2 only, and once. She says it herself, in her own words, with no token mechanics and
   * no suggestion that anything is wrong — and only in a natural gap, never over a live turn.
   */
  const speakBoundaryNotice = () => {
    const dc = dataChannelRef.current;
    const prompt = boundaryRef.current?.fence2_prompt;
    if (handoffSpokenRef.current || !prompt || !dc || dc.readyState !== 'open') return;
    handoffSpokenRef.current = true;
    try {
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: prompt }] }
      }));
      dc.send(JSON.stringify({ type: 'response.create' }));
      console.log('[TalkHandoff] boundary notice spoken');
      sendVoiceDiagnostic('talk_boundary_spoken', null, null);
    } catch (err) {
      handoffSpokenRef.current = false;
    }
  };

  /**
   * Step into the next Talk.
   *
   * ORDER IS THE WHOLE SAFETY PROPERTY. The next Talk is obtained from the provider FIRST,
   * while the current one is still live and untouched. Only once a real grant is in hand is the
   * current conversation let go — and it is let go properly: its transcript ends on its normal
   * terms, so Keep or let-it-disappear remains available for the usual six hours. The two
   * transcripts are never merged.
   *
   * If the grant fails, nothing has happened at all. The member is still in the conversation
   * they were in, and may keep talking or try again.
   */
  const acceptHandoff = async () => {
    if (handoffBusyRef.current) return;
    handoffBusyRef.current = true;
    const origin = sessionIdRef.current;
    setHandoffPhase('moving');
    try {
      const res = await fetch(`${API_URL}/api/realtime/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return null; } })(),
          handoff_from: origin
        })
      });
      if (!res.ok) throw new Error(`next talk unavailable (${res.status})`);
      const next = await res.json();
      if (!next?.client_secret?.value) throw new Error('next talk returned no grant');

      // The next Talk exists. Only now does the current one end.
      await closeMeter('ended');
      await endTranscript();
      sessionIdRef.current = null;
      transcriptIdRef.current = null;
      cleanupWebRTCResources();

      prefetchedSessionRef.current = next;
      pendingCarryRef.current = next.handoff || null;
      if (!isMountedRef.current) return;
      setState(STATE.INITIALIZING);
      await initializeConnection();
    } catch (err) {
      console.warn('[TalkHandoff] could not open the next Talk:', err?.message);
      sendVoiceDiagnostic('talk_handoff_failed', 'next_room_unavailable', err?.message || null);
      if (isMountedRef.current) setHandoffPhase('failed');
    } finally {
      handoffBusyRef.current = false;
    }
  };

  /**
   * Set aside. At Fence 1 the offer is not made again, and nothing about the conversation
   * changes; the single permitted re-surface is a materially different state — the provider has
   * actually reached its ceiling — and it can happen at most once. At Fence 2 the call really is
   * going to end, so setting it aside quietens it for a while rather than for good; she is never
   * asked to say it again either way.
   */
  const declineHandoff = () => {
    if (handoffOffer === 'boundary') {
      boundaryHushUntilRef.current = Date.now() + BOUNDARY_HUSH_MS;
    } else {
      handoffDeclinedRef.current = true;
    }
    setHandoffOffer(null);
    setHandoffPhase('idle');
    sendVoiceDiagnostic('talk_handoff_declined', null, null);
  };

  /**
   * Where this conversation actually stands, measured against the server's own thresholds.
   * The client holds no rule: it compares the provider's reported context size and the wall
   * clock to numbers it was given, and nothing here can shorten or interrupt a call.
   */
  useEffect(() => {
    const live = state === STATE.CONNECTED || state === STATE.LISTENING
      || state === STATE.THINKING || state === STATE.SPEAKING;
    if (!live) return;

    const tick = () => {
      const b = boundaryRef.current;
      if (!b || handoffBusyRef.current || !sessionOpenedAtRef.current) return;
      const elapsed = (Date.now() - sessionOpenedAtRef.current) / 1000;
      const tokens = reportedInputTokensRef.current;

      // FENCE 2 — inside the margin on the provider's hard session cap.
      if (elapsed >= b.fence2_elapsed_seconds) {
        if (Date.now() >= boundaryHushUntilRef.current) setHandoffOffer('boundary');
        if (state === STATE.CONNECTED) speakBoundaryNotice();
        return;
      }
      // FENCE 1 — visual, optional, and never announced over a spoken exchange.
      if (state !== STATE.CONNECTED) return;
      if (!handoffDeclinedRef.current) {
        if (tokens >= b.fence1_input_tokens) setHandoffOffer('context');
      } else if (!handoffRenotifiedRef.current && tokens >= b.renotify_input_tokens) {
        handoffRenotifiedRef.current = true;
        setHandoffOffer('context');
      }
    };

    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [state]);

  /**
   * Retry connection after failure
   */
  const handleRetry = () => {
    console.log('[Realtime] Retry requested - cleaning up stale state...');
    
    // Clean up all existing WebRTC resources
    cleanupWebRTCResources();
    
    // Clear error state
    setError(null);
    
    // Small delay to ensure cleanup completes before re-init
    setTimeout(() => {
      setState(STATE.INITIALIZING);
      initializeConnection();
    }, 100);
  };

  /**
   * Toggle microphone mute
   */
  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  /**
   * Close the session and cleanup
   * Shows privacy modal to let user decide about transcript
   */
  const handleClose = async () => {
    // Guard against double-close (can happen on mobile with gesture + button).
    // B2: the latch must not survive a close that never completed. `closeMeter` and
    // `endTranscript` below are awaited network calls, so a failed or stalled attempt used to
    // leave this latch set for good and every later tap was swallowed silently — a visible X
    // that could no longer close. A latch older than the stall window is re-enterable.
    // Successful closes are unaffected: the surface hands off to onClose or to the privacy
    // modal long before the window elapses.
    if (isClosingRef.current
        && Date.now() - closeLatchedAtRef.current < CLOSE_LATCH_STALL_MS) {
      console.log('[Realtime] Already closing, ignoring duplicate close');
      return;
    }
    isClosingRef.current = true;
    closeLatchedAtRef.current = Date.now();
    
    // Graceful end: close the meter first so the grant is finalised as "ended" rather than
    // swept as interrupted. Charged time is unchanged either way — it was banked per beat.
    await closeMeter('ended');
    
    // First, check if we have a transcript to handle BEFORE cleanup
    // This prevents the component from potentially unmounting before the modal shows
    let transcriptData = null;
    if (sessionIdRef.current) {
      try {
        transcriptData = await endTranscript();
        console.log('[Realtime] Transcript end result:', transcriptData);
        // Clear session ID to prevent duplicate calls
        sessionIdRef.current = null;
      } catch (err) {
        console.warn('[Realtime] Failed to end transcript:', err);
      }
    }
    
    // Now do cleanup
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
    }
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    // 3.5.5 — route watchers and the audio element are session-scoped; nothing they
    // installed may outlive the session.
    if (deviceChangeHandlerRef.current) {
      try {
        navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandlerRef.current);
      } catch (e) { /* nothing to remove */ }
      deviceChangeHandlerRef.current = null;
    }
    if (disconnectGraceRef.current) {
      clearTimeout(disconnectGraceRef.current);
      disconnectGraceRef.current = null;
    }
    if (connectDeadlineRef.current) {
      clearTimeout(connectDeadlineRef.current);
      connectDeadlineRef.current = null;
    }
    if (transcriptRetryTimerRef.current) {
      clearTimeout(transcriptRetryTimerRef.current);
      transcriptRetryTimerRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.onpause = null;
      audioElementRef.current.pause();
      audioElementRef.current.srcObject = null;
      audioElementRef.current.parentNode?.removeChild(audioElementRef.current);
      audioElementRef.current = null;
    }
    
    setRemoteStream(null);
    console.log('[Realtime] Setting state to CLOSED');
    setState(STATE.CLOSED);
    setTalkActivity(null);
    
    // Show privacy modal if we have messages
    if (transcriptData && transcriptData.message_count > 0) {
      console.log('[Realtime] Showing privacy modal, messages:', transcriptData.message_count);
      
      // Use flushSync to force synchronous state updates before any potential unmount
      flushSync(() => {
        setTranscriptInfo(transcriptData);
        setShowPrivacyModal(true);
      });

      // The continuity write for this session is already in flight.
      checkMemoryAdmission({ wait: true });

      return; // Don't call onClose yet - wait for user decision
    }
    
    // If no transcript or no messages, close immediately
    console.log('[Realtime] No transcript to save, closing');
    onClose?.();
  };
  
  /**
   * Handle privacy modal close (skip decision)
   */
  const handlePrivacyModalClose = () => {
    setShowPrivacyModal(false);
    onClose?.();
  };

  // Initialize connection on mount - only run once
  useEffect(() => {
    isMountedRef.current = true;

    // F-410-B. A cold mount after an unexpected client teardown must not open a competing
    // session on top of the conversation the member just lost. Before connecting anything, ask
    // whether an interrupted Talk is waiting for their decision — declaring plainly that this
    // client holds no live transport, which is what stops a vanished session being classified
    // as still happening. If one is waiting, hand control back to the Talk surface, where the
    // accepted recovery decision already lives, instead of holding an orb or starting a rival
    // session. This check never blocks Talk: any failure falls through and connects as before.
    const connectUnlessRecoveryIsOwed = async () => {
      try {
        const res = await fetchWithAuthRetry(
          `${API_URL}/api/realtime/transcript/pending?live=1`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          if (data?.pending && data?.message_count > 0) {
            if (!isMountedRef.current) return;
            // F-410-B retest repair. STAY on the Talk surface. The accepted recovery offer is
            // mounted here as a sibling of this component, so leaving would unmount the very
            // decision the member needs — which is exactly what locked Talk on the device:
            // `offer_shown` followed instantly by `discarded_unmounted`, on every tap. We simply
            // do not connect. The offer renders over this surface, the member decides, and the
            // surface remounts this component to connect.
            setState(STATE.CLOSED);       // no session created, nothing claimed
            return;
          }
        }
      } catch (err) {
        // Recovery must never be the reason Talk cannot start.
      }
      if (!isMountedRef.current) return;
      initializeConnection();
    };

    connectUnlessRecoveryIsOwed();
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      // An unmount that never went through the close path would drop the closing delta.
      closeMeterBeacon('interrupted');
      // Cleanup resources
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
      }
      if (peerConnectionRef.current) {
        // Stop anything still attached to the connection, in case the local
        // stream ref was never assigned (setup interrupted mid-flight).
        peerConnectionRef.current.getSenders?.().forEach((sender) => {
          if (sender.track) sender.track.stop();
        });
        peerConnectionRef.current.close();
      }
      if (deviceChangeHandlerRef.current) {
        try {
          navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandlerRef.current);
        } catch (e) { /* nothing to remove */ }
        deviceChangeHandlerRef.current = null;
      }
      if (disconnectGraceRef.current) {
        clearTimeout(disconnectGraceRef.current);
        disconnectGraceRef.current = null;
      }
      if (connectDeadlineRef.current) {
        clearTimeout(connectDeadlineRef.current);
        connectDeadlineRef.current = null;
      }
      if (audioElementRef.current) {
        audioElementRef.current.onpause = null;
        audioElementRef.current.pause();
        audioElementRef.current.srcObject = null;
        audioElementRef.current.parentNode?.removeChild(audioElementRef.current);
        audioElementRef.current = null;
      }
    };
  }, []); // Empty deps - only run on mount/unmount

  /**
   * A reload, a tab discard or the browser being backgrounded and dropped never runs the close
   * path. `pagehide` is the last event Safari gives us, so mark the session ended there with a
   * keepalive request — the same transition the close path performs, nothing more. If even this
   * doesn't land, the server-side reclaim catches it on the next visit.
   */
  useEffect(() => {
    const markEndedOnUnload = () => {
      // Bank the closing delta before anything else: this is the last moment we have.
      closeMeterBeacon('interrupted');
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;
      try {
        fetch(`${API_URL}/api/realtime/transcript/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          keepalive: true,
          body: JSON.stringify({ session_id: sessionId })
        });
      } catch (err) {
        // Nothing to do here — the reclaim path is the backstop.
      }
    };
    window.addEventListener('pagehide', markEndedOnUnload);
    return () => window.removeEventListener('pagehide', markEndedOnUnload);
  }, []);

  /**
   * Backgrounding. A hidden tab has its timers throttled or stopped, so the heartbeat can stop
   * arriving while the session is still open. Beat once on the way out and once on the way
   * back, so the time either side of the gap is banked at the moment it is known rather than
   * being clamped away. The clamp still applies to the gap itself, so nothing can be inflated.
   */
  useEffect(() => {
    const onVisibility = () => {
      if (!meterOpenedRef.current) return;
      sendMeterBeat();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Get state label - minimal, only show during connection
  // Once connected, the orb communicates presence - no text needed
  const getStateLabel = () => {
    switch (state) {
      case STATE.INITIALIZING:
      case STATE.CONNECTING:
        return lang === 'es' ? 'Conectando...' : 'Connecting...';
      case STATE.ERROR:
        return ''; // Error message shown separately
      default:
        return ''; // No label once connected - orb communicates state
    }
  };

  // Determine orb visual state
  const handoffCopy = HANDOFF_COPY[lang] || HANDOFF_COPY.en;

  const getOrbState = () => {
    if (state === STATE.SPEAKING || isArvexisSpeaking) return 'speaking';
    if (state === STATE.THINKING) return 'thinking';
    if (state === STATE.LISTENING) return 'listening';
    // Phase 7 Bundle 5: the brief calm return after she has spoken, then rest.
    if (state === STATE.CONNECTED) return Date.now() < settlingUntil ? 'settling' : 'idle';
    if (state === STATE.ERROR) return 'waiting'; // 5.2 truth guard: ERROR -> waiting
    return 'waiting';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[120] flex flex-col items-center justify-center"
        style={{
          background: 'linear-gradient(to bottom, #0a0a0f 0%, #12121a 50%, #0a0a0f 100%)'
        }}
        data-testid="realtime-voice-mode"
      >
        {/* Close button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          onClick={handleClose}
          className="absolute right-6 p-3 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
          // B1: index.html opts the app into viewport-fit=cover, so a flat 24px put this control
          // under the status bar / Dynamic Island, where taps never reach the page. Same inset
          // expression already used by every other full-screen surface.
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}
          data-testid="realtime-close-button"
        >
          <X className="w-6 h-6 text-white/60" />
        </motion.button>

        {/* Mute toggle */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          onClick={toggleMute}
          className={`absolute left-6 p-3 rounded-full transition-colors ${
            isMuted ? 'bg-red-500/20 hover:bg-red-500/30' : 'bg-white/5 hover:bg-white/10'
          }`}
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}
          data-testid="realtime-mute-button"
        >
          {isMuted ? (
            <MicOff className="w-6 h-6 text-red-400" />
          ) : (
            <Mic className="w-6 h-6 text-white/60" />
          )}
        </motion.button>

        {/* Central presence orb — the sole indicator of Arvexis's state, and the primary
            expression of the move itself: while the next Talk is being established it settles
            and dims rather than handing the moment to a spinner. */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{
            scale: handoffPhase === 'moving' ? 0.955 : 1,
            opacity: handoffPhase === 'moving' ? 0.58 : 1,
          }}
          transition={{ duration: handoffPhase === 'moving' ? 1.6 : 0.6, ease: 'easeOut' }}
          className="relative"
        >
          <ArvexisPresence 
            size="large"
            audioVolume={arvexisVolume}
            memberVolume={state === STATE.LISTENING ? memberVolumeRaw : 0}
            state={getOrbState()}
          />
        </motion.div>

        {/* 4.3 — invisible work, named truthfully. Only while a real tool call is running; the
            orb alone carries listening / thinking / speaking. */}
        <AnimatePresence>
          {talkActivity && (
            <motion.div
              key={talkActivity}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3 }}
              className="mt-8 flex items-center gap-2"
              data-testid="talk-activity-indicator"
              data-activity={talkActivity}
              role="status"
              aria-live="polite"
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: 'rgba(230, 192, 110, 0.55)' }}
                animate={{ opacity: [0.4, 0.85, 0.4] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span className="text-white/40 text-sm font-light tracking-wide">
                {activityLabel(talkActivity, lang)}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* State indicator - only shown during connection or error. A move in progress is
            carried by the orb and one quiet line, so no "Connecting…" appears over it. */}
        {(state === STATE.INITIALIZING || state === STATE.CONNECTING || state === STATE.ERROR)
          && !(handoffPhase === 'moving' && state !== STATE.ERROR) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center"
          >
            {state !== STATE.ERROR ? (
              <div className="flex items-center gap-2 justify-center">
                <div className="w-2 h-2 rounded-full bg-amber-400/60 animate-pulse" />
                <p className="text-white/40 text-sm font-light tracking-wide">
                  {getStateLabel()}
                </p>
              </div>
            ) : capacityLimit ? (
              /* Capacity, stated neutrally. No red, no retry, no urgency, no upgrade pressure —
                 and an explicit statement that the relationship is untouched. */
              <div
                className="flex flex-col items-center gap-3 max-w-sm mx-auto"
                data-testid="talk-capacity-notice"
              >
                <p className="text-white/60 text-sm font-light leading-relaxed text-center">
                  {capacityLimit}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <p className="text-red-400/60 text-sm">
                  {error || 'Connection failed'}
                </p>
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 text-xs text-white/60 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                  data-testid="realtime-retry-button"
                >
                  {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
                </button>
              </div>
            )}
          </motion.div>
        )}
        
        {/* PHASE 7 BUNDLE 5 — continuity at the real Talk boundary, in the Founder-approved
            Talk-continuity visual language: atmospheric midnight glass, one restrained champagne
            accent, ivory and soft-silver type, and always visually secondary to the orb. An
            invitation, never a warning. No percentages, counts, countdowns, rings or spinners. */}
        <AnimatePresence>
          {handoffOffer && state !== STATE.ERROR && (
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="absolute bottom-16 left-5 right-5 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[430px]"
              data-testid="talk-handoff-offer"
              data-fence={handoffOffer}
              data-phase={handoffPhase}
              role="status"
              aria-live="polite"
            >
              <div
                className="relative overflow-hidden rounded-[24px]"
                style={{
                  border: `1px solid rgba(230, 192, 110, ${handoffOffer === 'boundary' ? 0.26 : 0.15})`,
                  background:
                    'linear-gradient(158deg, rgba(16,21,35,0.78) 0%, rgba(8,10,18,0.9) 100%)',
                  backdropFilter: 'blur(26px) saturate(115%)',
                  WebkitBackdropFilter: 'blur(26px) saturate(115%)',
                  boxShadow: handoffOffer === 'boundary'
                    ? '0 28px 70px -34px rgba(0,0,0,0.95), 0 0 34px -18px rgba(230,192,110,0.30), inset 0 1px 0 rgba(255,255,255,0.055)'
                    : '0 24px 62px -34px rgba(0,0,0,0.92), inset 0 1px 0 rgba(255,255,255,0.045)',
                }}
              >
                {/* a single champagne hairline, brighter at the boundary — the only hierarchy cue */}
                <div
                  className="absolute inset-x-0 top-0 h-px pointer-events-none"
                  style={{
                    background: `linear-gradient(90deg, rgba(230,192,110,0) 0%, rgba(230,192,110,${
                      handoffOffer === 'boundary' ? 0.5 : 0.26}) 50%, rgba(230,192,110,0) 100%)`,
                  }}
                />

                {handoffPhase === 'moving' ? (
                  /* The orb is the expression of the movement. This is one quiet line, not a
                     progress indicator. */
                  <div className="px-6 py-6 flex items-center gap-3.5" data-testid="talk-handoff-moving">
                    <motion.span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: 'rgba(230,192,110,0.7)' }}
                      animate={{ opacity: [0.35, 0.9, 0.35] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <span
                      className="text-[0.95rem] font-light tracking-wide"
                      style={{ color: 'rgba(226,232,240,0.72)' }}
                    >
                      {handoffCopy.moving}
                    </span>
                  </div>
                ) : (
                  <div className="px-5 pt-5 pb-5">
                    <div className="flex items-start gap-4">
                      <div
                        className="shrink-0 mt-0.5 w-[38px] h-[38px] rounded-full flex items-center justify-center"
                        style={{
                          border: '1px solid rgba(230,192,110,0.32)',
                          background: 'radial-gradient(circle at 50% 40%, rgba(230,192,110,0.10) 0%, rgba(230,192,110,0) 70%)',
                        }}
                        aria-hidden="true"
                      >
                        {handoffOffer === 'boundary' && handoffPhase !== 'failed' ? (
                          <Clock className="w-[17px] h-[17px]" style={{ color: '#E6C06E' }} />
                        ) : (
                          <InfinityIcon className="w-[19px] h-[19px]" style={{ color: '#E6C06E' }} />
                        )}
                      </div>

                      <div className="min-w-0 flex-1 pr-6">
                        <p
                          className="leading-snug"
                          style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: handoffOffer === 'boundary' ? '1.28rem' : '1.2rem',
                            color: '#F4EFE6',
                            letterSpacing: '0.01em',
                          }}
                          data-testid="talk-handoff-title"
                        >
                          {(handoffPhase === 'failed' ? handoffCopy.failed
                            : handoffCopy[handoffOffer]).title}
                        </p>
                        <p
                          className="mt-1.5 text-sm font-light leading-relaxed"
                          style={{ color: 'rgba(226,232,240,0.60)' }}
                        >
                          {(handoffPhase === 'failed' ? handoffCopy.failed
                            : handoffCopy[handoffOffer]).body}
                        </p>
                      </div>

                      {handoffPhase !== 'failed' && (
                        <button
                          onClick={declineHandoff}
                          className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-white/5"
                          data-testid="talk-handoff-dismiss"
                          aria-label={handoffCopy.dismiss}
                        >
                          <X className="w-4 h-4" style={{ color: 'rgba(226,232,240,0.38)' }} />
                        </button>
                      )}
                    </div>

                    <div className="mt-5 flex items-center gap-3">
                      <button
                        onClick={acceptHandoff}
                        className="flex-1 py-[11px] rounded-full text-[0.9rem] font-medium transition-all duration-300 hover:brightness-110"
                        style={{
                          background: 'linear-gradient(180deg, #F0D79C 0%, #E0B36A 100%)',
                          color: '#1B1608',
                          boxShadow: '0 10px 26px -12px rgba(224,179,106,0.55)',
                        }}
                        data-testid="talk-handoff-accept"
                      >
                        {(handoffPhase === 'failed' ? handoffCopy.failed
                          : handoffCopy[handoffOffer]).primary}
                      </button>
                      {handoffPhase !== 'failed' && (
                        <button
                          onClick={declineHandoff}
                          className="flex-1 py-[11px] rounded-full text-[0.9rem] font-light transition-colors duration-300"
                          style={{
                            border: '1px solid rgba(255,255,255,0.08)',
                            background: 'rgba(255,255,255,0.025)',
                            color: 'rgba(226,232,240,0.70)',
                          }}
                          data-testid="talk-handoff-decline"
                        >
                          {handoffCopy[handoffOffer].secondary}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Crisis resources — shown quietly beside the conversation, never interrupting it */}
        {safetyNotice && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-20 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[420px] rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-5"
            data-testid="talk-safety-notice"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-white/80 text-sm font-light leading-relaxed">
                {safetyNotice.message}
              </p>
              <button
                onClick={() => setSafetyNotice(null)}
                className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
                data-testid="talk-safety-notice-dismiss"
                aria-label={lang === 'es' ? 'Cerrar' : 'Dismiss'}
              >
                <X className="w-4 h-4 text-white/40" />
              </button>
            </div>
            <div className="mt-3 space-y-1.5">
              {(safetyNotice.resources || []).map((r) => (
                <p key={r.name} className="text-white/60 text-xs">
                  <span className="text-[#E6C06E]">{r.number || r.action}</span>
                  {' — '}{r.name}
                </p>
              ))}
            </div>
            {safetyNotice.closing && (
              <p className="mt-3 text-white/50 text-xs font-light">{safetyNotice.closing}</p>
            )}
          </motion.div>
        )}
        {/* DURABILITY (Talk transcript fix) — the one thing the member was never told. The call
            continues untouched; this only says the conversation is not being saved right now. */}
        {transcriptDegraded && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-20 left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[420px] rounded-2xl border border-[#E6C06E]/25 bg-black/60 backdrop-blur-xl px-5 py-4"
            data-testid="talk-transcript-degraded-notice"
          >
            <p className="text-white/70 text-sm font-light leading-relaxed">
              {lang === 'es'
                ? 'Sigue hablando — esta parte de la conversación aún no se ha guardado. Lo estoy intentando de nuevo.'
                : "Keep talking — this part of the conversation isn't saved yet. Still trying."}
            </p>
          </motion.div>
        )}

      </motion.div>
      
      {/* Privacy Modal — Private by Default */}
      <VoicePrivacyModal
        isOpen={showPrivacyModal}
        onClose={handlePrivacyModalClose}
        onSave={saveTranscript}
        onLetExpire={letTranscriptExpire}
        transcriptInfo={transcriptInfo}
        isLoading={isSubmittingDecision}
        memoryAdmission={memoryAdmission}
      />
    </AnimatePresence>
  );
};

export default RealtimeVoiceMode;
